// src/pages/dashboard/Dashboard.jsx

import React, { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../services/AuthService';
import AuthContext from '../../context/Auth/AuthContext';
import Modal from './modal/modal';
import '../dashboard/dashboards.css';
import "@fortawesome/fontawesome-free/css/all.css";

const Dashboard = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const headerRef   = useRef(null);
  const itemRef     = useRef(null);
  const { user } = useContext(AuthContext);

  const getStatusColor = certidoes => {
    const isComplete = c => c.uuid && c.uuid !== 'ERRO' && c.uuid !== 'reprocessamento';
    const all  = certidoes.every(isComplete);
    const some = certidoes.some(c => c.uuid);
    return all ? 'green' : some ? 'yellow' : 'red';
  };
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedData, setExpandedData] = useState(null);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  const [itemsPerPage, setItemsPerPage]   = useState(20);
  const [currentPage, setCurrentPage]     = useState(1);

  // Função de busca dos dados
  const fetchDashboardData = async () => {
    if (!user?.user?.id) {
      setErrorMessage("Erro: Usuário não está autenticado corretamente.");
      setLoading(false);
      return;
    }
    const payload = { user_ids: [user.user.id] };
    try {
      const [fisicoResponse, juridicoResponse] = await Promise.all([
        AuthService.fetchDashboardFisico(payload),
        AuthService.fetchDashboardJuridico(payload)
      ]);
      const combined = [...fisicoResponse, ...juridicoResponse];
      combined.sort((a, b) => new Date(b.creation_date) - new Date(a.creation_date));
      setDashboardData(combined);
    } catch {
      setErrorMessage('Erro ao carregar os dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Carrega os dados ao montar e quando o usuário muda
  useEffect(() => {
    fetchDashboardData();
  }, [user?.user?.id]);

  // Fecha dropdown de status ao clicar fora
  useEffect(() => {
    if (!showStatusDropdown) return;
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showStatusDropdown]);

  // Recalcula quantos itens cabem na tela
  useEffect(() => {
    const calculateItemsPerPage = () => {
      if (!headerRef.current || !itemRef.current) return;
      const headerH   = headerRef.current.offsetHeight;
      const footerH   = 0; // footer global no Home
      const itemH     = itemRef.current.offsetHeight;
      const available = window.innerHeight - headerH - footerH;
      const count     = Math.floor(available / itemH);
      setItemsPerPage(count > 0 ? count : 1);
    };
    calculateItemsPerPage();
    window.addEventListener('resize', calculateItemsPerPage);
    return () => window.removeEventListener('resize', calculateItemsPerPage);
  }, []);

  // Handlers simples
  const handleReload        = () => { setLoading(true); fetchDashboardData(); };
  const handleChangeSearch  = e => setSearch(e.target.value);
  const handleModalOpen     = data => setExpandedData(data);
  const handleModalClose    = ()   => setExpandedData(null);

  // Filtra pelos status e pela pesquisa
  const filteredData = dashboardData
    .filter(item => {
      if (statusFilter === 'all') return true;
      return getStatusColor(item.certidoes) === statusFilter;
    })
    .filter(item => {
      if (!search.trim()) return true;
      const s = search.toLowerCase().trim();
      return (
        item.nome?.toLowerCase().includes(s) ||
        item.cpf?.replace(/[\.-]/g,'').includes(s) ||
        item.cnpj?.replace(/[\.-]/g,'').includes(s)
      );
    });

  // Reseta página ao trocar filtro/pesquisa
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, search]);

  // Paginador
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const pageData   = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Loading / Erro
  if (loading)       return <div>Carregando...</div>;
  if (errorMessage)  return <div>{errorMessage}</div>;

  // Renderiza cada card, anotando ref no primeiro
  const renderDashboardItem = (data, index) => {
    const color = getStatusColor(data.certidoes);
    const label = color === 'green'
      ? 'Concluído'
      : color === 'yellow'
        ? 'Em andamento'
        : 'Na fila';
    const formatDate = d => new Date(d).toLocaleDateString('pt-BR');

    const handleDownloadAll = async () => {
      const uuids = data.certidoes.map(c => c.uuid);
      try {
        await AuthService.downloadArquivosZip(uuids);
      } catch {
        alert('Erro ao baixar todas as certidões.');
      }
    };

    const refProps = index === 0 ? { ref: itemRef } : {};

    return (
      <div
        key={data.lote}
        className="dashboard-item"
        onClick={() => handleModalOpen(data)}
        {...refProps}
      >
        <div className="dashboard-item-row">
          <div className="dashboard-item-content">
            <h3>
              {data.cpf
                ? data.nome
                : data.cnpj
                  ? data.nome_empresa
                  : 'Nome não disponível'}
            </h3>
            <div className="dashboard-item-details">
              <span>{data.cpf ? `CPF: ${data.cpf}` : `CNPJ: ${data.cnpj}`}</span>
              <span className="dot-separator">•</span>
              <span>Data da Solicitação: {formatDate(data.creation_date)}</span>
            </div>
          </div>
          <div className="dashboard-item-status">
            <span className={`dashboard-status-badge ${color}`}>{label}</span>
            {color === "green" && (
              <svg
                className="download-icon"
                width="21" height="21"
                viewBox="0 0 24 24"
                fill="none"
                onClick={e => { e.stopPropagation(); handleDownloadAll(); }}
              >
                <path d="M12 4v10" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                <path d="M6 12l6 6 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                <rect x="5" y="18" width="14" height="2" rx="1" fill="#fff"/>
              </svg>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div ref={headerRef} className="dashboard-header">
        <div className="header-row--top">
          <div className="header-titles">
            <h1>Certidão</h1>
            <h2 className="dashboard-subtitle">Gerencie as certidões solicitadas</h2>
          </div>
          <button
            className="request-btn"
            onClick={() => navigate('/home/consult')}
          >
            Solicitar Certidão
          </button>
        </div>
        <div className="header-row--bottom">
          <div className="header-actions">
            <input
              type="text"
              placeholder="Pesquisar por nome, CPF ou CNPJ"
              value={search}
              onChange={handleChangeSearch}
              className="search-input"
            />
            <div className="dropdown-status-filter" ref={dropdownRef}>
              <button
                className="dropdown-toggle"
                onClick={() => setShowStatusDropdown(s => !s)}
              >
                <span className="dropdown-text">
                  {statusFilter === 'all'   && 'Todos'}
                  {statusFilter === 'green' && 'Concluído'}
                  {statusFilter === 'yellow'&& 'Em andamento'}
                  {statusFilter === 'red'   && 'Na fila para processamento'}
                </span>
                <span className="dropdown-arrow">▼</span>
              </button>
              {showStatusDropdown && (
                <ul className="dropdown-menu-status">
                  {['all','green','yellow','red'].map(sf => (
                    <li
                      key={sf}
                      className={statusFilter === sf ? 'selected' : ''}
                      onClick={() => { setStatusFilter(sf); setShowStatusDropdown(false); }}
                    >
                      {{
                        all: 'Todos',
                        green: 'Concluído',
                        yellow: 'Em andamento',
                        red: 'Na fila para processamento'
                      }[sf]}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <i
              className="fas fa-sync-alt reload-icon"
              onClick={handleReload}
              title="Recarregar"
            />
          </div>
        </div>
      </div>

      <div className="dashboard-div">
        <div className="dashboard-container">
          {pageData.length > 0
            ? pageData.map((d, i) => renderDashboardItem(d, i))
            : <p style={{ color: "#fff", textAlign: "center", margin: "32px 0" }}>
                Nenhuma certidão encontrada
              </p>
          }
        </div>
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              « Anterior
            </button>
            <span>{currentPage} de {totalPages}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Próximo »
            </button>
          </div>
        )}
      </div>

      {expandedData && <Modal data={expandedData} onClose={handleModalClose} onReprocess={handleReload} />}
    </>
  );
};

export default Dashboard;
